import { acknowledgeAlert } from '@/api/mutations'
import { queryKeys, siteAlertsQuery, siteQuery } from '@/api/queries'
import type { AlertsListResponse } from '@/api/schemas'
import { AlertsHistory } from '@/components/alerts/AlertsHistory'
import { ErrorCard, LoadingCard } from '@/components/ui/StateCards'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { t } from '@/lib/i18n'

const searchSchema = z.object({
  pending: z.boolean().optional(),
})

export const Route = createFileRoute('/sites/$siteId/alerts')({
  validateSearch: searchSchema,
  component: AlertsRoute,
})

function AlertsRoute() {
  const { siteId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/sites/$siteId/alerts' })
  const pendingOnly = search.pending ?? false

  const alertsQ = useQuery(siteAlertsQuery(siteId))
  const siteQ = useQuery(siteQuery(siteId))
  const queryClient = useQueryClient()
  const [acking, setAcking] = useState<Set<number>>(new Set())

  const ackMutation = useMutation({
    mutationFn: (alertId: number) => acknowledgeAlert(siteId, alertId),
    onMutate: async (alertId) => {
      setAcking((prev) => new Set(prev).add(alertId))
      await queryClient.cancelQueries({ queryKey: queryKeys.siteAlerts(siteId) })
      const previous = queryClient.getQueryData<AlertsListResponse>(queryKeys.siteAlerts(siteId))
      if (previous) {
        queryClient.setQueryData<AlertsListResponse>(queryKeys.siteAlerts(siteId), {
          alerts: previous.alerts.map((a) => (a.id === alertId ? { ...a, acknowledged: true } : a)),
        })
      }
      return { previous }
    },
    onError: (_err, _alertId, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(queryKeys.siteAlerts(siteId), ctx.previous)
      }
    },
    onSettled: (_data, _err, alertId) => {
      setAcking((prev) => {
        const next = new Set(prev)
        next.delete(alertId)
        return next
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.siteAlerts(siteId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.site(siteId) })
    },
  })

  if (alertsQ.isPending || siteQ.isPending) return <LoadingCard label={t('Loading alerts…')} />
  if (alertsQ.isError)
    return <ErrorCard message={alertsQ.error.message} onRetry={alertsQ.refetch} />
  if (siteQ.isError) return <ErrorCard message={siteQ.error.message} onRetry={siteQ.refetch} />

  return (
    <AlertsHistory
      siteId={siteId}
      siteName={siteQ.data.site.name}
      alerts={alertsQ.data.alerts}
      pendingOnly={pendingOnly}
      onTogglePending={(next) => navigate({ search: next ? { pending: true } : {} })}
      onAcknowledge={(id) => ackMutation.mutate(id)}
      acking={acking}
    />
  )
}
